/**
 * MediaRequestsContext — the one request list the media canvas reads from.
 *
 * Provided once around the canvas so the requests surface and every library
 * shelf and grid share a single poll. Each of those reading the endpoint for
 * itself would re-dial every user's request manager once per consumer, which on
 * a box with several libraries is a fan-out nobody asked for — and the requests
 * page, which renders inside this provider, would poll the same endpoint twice
 * on its own.
 *
 * Reading requests is a granted capability, so the provider fetches nothing at
 * all for a caller without it: a viewer never sees another person's request
 * leak into their library view.
 */
import { createContext, useContext, useMemo, useState, type ReactNode } from "react";

import { useMe } from "../hooks/useMe";
import { hasPermission, isAdminUser } from "../lib/permissions";
import { useMediaRequests } from "../hooks/useMediaRequests";
import {
  buildLibraryIdSet,
  selectGhostBreakdown,
  byMostRecent,
  type EnrichedMediaRequest,
  type GhostBreakdown,
} from "../lib/mediaRequests/canvas";
import type {
  MediaRequestDefaultView,
  MediaRequestError,
  MediaRequestHistoryNotice,
  MediaRequestMediaType,
  MediaRequestProblem,
  MediaRequestScope,
} from "../hooks/useMediaRequests";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** A library item as far as request correlation is concerned. */
type CorrelatableItem = { tmdbId: string | null; tvdbId: string | null };

type MediaRequestsContextValue = {
  /** False when the caller may not read requests. Nothing is fetched. */
  enabled: boolean;
  requests: EnrichedMediaRequest[];
  errors: MediaRequestError[];
  problems: MediaRequestProblem[];
  /** Purged, identity-less fulfilled-history counts, surfaced as list metadata. */
  historyNotices: MediaRequestHistoryNotice[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Box-wide is an admin scope; a grantee is held to their own. */
  canReadAll: boolean;
  /**
   * Whether a landed request may offer a Media Portal link. False when
   * streaming is off or no media server is connected, so the loop-closing link
   * is withheld rather than pointed at a target that would not resolve.
   */
  portalLinksEnabled: boolean;
  /**
   * The admin-configured default tab the requests list opens on, and how many
   * already-in-library items it pages at a time. Carried on the list envelope,
   * so a grantee who cannot read settings still gets the configured behaviour.
   */
  defaultView: MediaRequestDefaultView;
  inLibraryPageSize: number;
  scope: MediaRequestScope;
  setScope: (scope: MediaRequestScope) => void;
  /**
   * The requests that belong inline beside a given set of library items, with
   * the counts that explain what was left out.
   *
   * Deduplicates against the items it is handed, so a title that is both
   * requested and present appears once — as the real library card, which wins.
   * The counts let a surface showing fewer requests than the requests page say
   * why, instead of appearing to contradict it.
   */
  ghostBreakdownFor: (
    items: CorrelatableItem[],
    mediaType?: MediaRequestMediaType,
  ) => GhostBreakdown;
  /** The request a library item arrived through, when it arrived through one. */
  landedFor: (item: CorrelatableItem) => EnrichedMediaRequest | null;
};

const EMPTY: MediaRequestsContextValue = {
  enabled: false,
  requests: [],
  errors: [],
  problems: [],
  historyNotices: [],
  loading: false,
  error: null,
  refresh: () => {},
  canReadAll: false,
  portalLinksEnabled: false,
  defaultView: "all",
  inLibraryPageSize: 24,
  scope: "own",
  setScope: () => {},
  ghostBreakdownFor: () => ({
    ghosts: [],
    inLibrary: 0,
    declined: 0,
    otherType: 0,
    considered: 0,
  }),
  landedFor: () => null,
};

const MediaRequestsCtx = createContext<MediaRequestsContextValue>(EMPTY);

export const useMediaRequestsContext = (): MediaRequestsContextValue =>
  useContext(MediaRequestsCtx);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const MediaRequestsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useMe();
  const canRead = hasPermission(user, "admin.requests.read");

  // Reaching this surface and seeing everyone's requests are separate grants,
  // and only the first is a permission. Asking for a scope you cannot have is
  // refused outright rather than narrowed, so a grantee asks for their own —
  // the only scope they can actually be served.
  const canReadAll = isAdminUser(user);
  const [scope, setScope] = useState<MediaRequestScope>(canReadAll ? "all" : "own");
  const effectiveScope: MediaRequestScope = canReadAll ? scope : "own";

  // `enabled` short-circuits the hook entirely, so a caller without the grant
  // costs no request at all rather than one that gets refused.
  const {
    requests,
    errors,
    problems,
    historyNotices,
    loading,
    error,
    refresh,
    portalLinksEnabled,
    defaultView,
    inLibraryPageSize,
  } = useMediaRequests(effectiveScope, canRead);

  const enriched = requests as EnrichedMediaRequest[];

  const value = useMemo<MediaRequestsContextValue>(() => {
    if (!canRead) return EMPTY;

    /**
     * Index by external id so a library item can be traced back to the request
     * it arrived through in constant time rather than by scanning the list per
     * card. Namespaced, because TMDb and TVDb numbering collide freely.
     */
    const byExternalId = new Map<string, EnrichedMediaRequest>();
    for (const request of enriched) {
      if (request.tmdbId !== undefined) byExternalId.set(`tmdb:${request.tmdbId}`, request);
      if (request.tvdbId !== undefined) byExternalId.set(`tvdb:${request.tvdbId}`, request);
    }

    return {
      enabled: true,
      requests: enriched,
      errors,
      problems,
      historyNotices,
      loading,
      error,
      refresh,
      canReadAll,
      portalLinksEnabled,
      defaultView,
      inLibraryPageSize,
      scope: effectiveScope,
      setScope,
      ghostBreakdownFor: (items, mediaType) => {
        const breakdown = selectGhostBreakdown(enriched, buildLibraryIdSet(items), mediaType);
        // Ghosts have no date added, no play count, and no size, so they are
        // never mixed into a library sort. They form their own trailing group
        // ordered by when they were asked for.
        return { ...breakdown, ghosts: [...breakdown.ghosts].sort(byMostRecent) };
      },
      landedFor: (item) => {
        const match =
          (item.tmdbId ? byExternalId.get(`tmdb:${item.tmdbId}`) : undefined) ??
          (item.tvdbId ? byExternalId.get(`tvdb:${item.tvdbId}`) : undefined);
        // Only a request that was actually granted explains an item being here.
        // A pending or denied request that happens to name the same title did
        // not put it on the server.
        if (!match) return null;
        return match.status === "approved" || match.status === "available" ? match : null;
      },
    };
  }, [canRead, enriched, errors, problems, historyNotices, loading, error, refresh, canReadAll, portalLinksEnabled, defaultView, inLibraryPageSize, effectiveScope]);

  return <MediaRequestsCtx.Provider value={value}>{children}</MediaRequestsCtx.Provider>;
};
